import { useEffect, useRef, useState } from 'react';
import { useModal } from '@/context/ModalContext';
import { useParams, useSearchParams } from 'react-router-dom';
import CustomModalOverlay from '@/components/common/Custom/CustomModalOverlay/CustomModalOverlay';
import Breadcrumb from '@/components/common/Breadcrumb/Breadcrumb';
import GlobalButton from '@/components/ui/GlobalButton';
import TextInput from '@/components/ui/TextInput/TextInput';
import TextArea from '@/components/ui/TextArea/TextArea';
import SelectInput from '@/components/ui/SelectInput/SelectInput';
import Skeleton from '@/components/ui/Skeleton/Skeleton';
import useInitialLoadFlag from '@/hooks/useInitialLoadFlag';
import PriorityBadge from '@/components/common/Badges/PriorityBadge/PriorityBadge';
import PopoverWithType from '@/components/ui/Popover/PopoverWithType';
import { DeptIcon } from '@/components/common/Badges/DepartmentBadge/DepartmentBadge';
import IntakeLinks from './IntakeLinks/IntakeLinks';
import IntakeComments from './IntakeComments/IntakeComments';
import Attachments from '../TaskModal/Attachments/Attachments';
import TeamSelectorModal from './TeamSelectorModal/TeamSelectorModal';
import IntakeService from '@/services/IntakeService';
import WebAppService from '@/services/WebAppService';
import { getErrorMessage } from '@/utils/helpers/helpers';
import { Check, CopyIcon } from 'lucide-react';
import type { Intake, Team } from '@/types';
import {
  BugFixIcon,
  CleanupIcon,
  FeatureIcon,
  ImprovementIcon,
  RedesignIcon,
} from '@/svgs/svgs';
import AssignTask from '../TaskModal/AssignTask/AssignTask';

const INTAKE_TYPE_OPTIONS = [
  { value: 'Bug', label: 'Bug', icon: <BugFixIcon size={16} /> },
  { value: 'Feature', label: 'Feature', icon: <FeatureIcon size={16} /> },
  { value: 'Redesign', label: 'Redesign', icon: <RedesignIcon size={16} /> },

  {
    value: 'Improvement',
    label: 'Improvement',
    icon: <ImprovementIcon size={16} />,
  },
  { value: 'Cleanup', label: 'Cleanup', icon: <CleanupIcon size={16} /> },
];

const getIntakeTeamId = (intake: Intake | null): string => {
  if (!intake?.team_id) return '';
  return typeof intake.team_id === 'string' ? intake.team_id : intake.team_id._id;
};

const getIntakeTeamName = (intake: Intake | null): string => {
  if (!intake?.team_id || typeof intake.team_id === 'string') {
    return 'Unknown Team';
  }

  return intake.team_id.team_name || 'Unknown Team';
};

export default function IntakeModal() {
  const { isOpen, closeModal, modalData } = useModal();
  const { team_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIntakeFromUrl = searchParams.get('selectedIntake');
  const intakeId = (modalData as any)?.intakeId;
  const onSuccess = (modalData as any)?.onSuccess as
    | (() => Promise<unknown> | unknown)
    | undefined;
  

  const [fetching, setFetching] = useState(false);
  const [hasLoadedOnce, markLoaded, resetLoaded] = useInitialLoadFlag(1000);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [intakeDetails, setIntakeDetails] = useState<Intake | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [editedValues, setEditedValues] = useState({
    title: '',
    description: '',
    acceptance_criteria: '',
    test_cases: '',
  });

  const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const open = isOpen('intake-details');
  const teamOptions = (() => {
    const baseOptions = teams.map((team) => ({
      label: String(team?.team_name || ''),
      value: String(team?._id || team?.id || ''),
    }));

    const currentTeamId = getIntakeTeamId(intakeDetails);
    const currentTeamName = getIntakeTeamName(intakeDetails);

    if (currentTeamId) {
      const otherOptions = baseOptions.filter(
        (opt) => opt.value !== currentTeamId
      );
      return [
        {
          label: currentTeamName,
          value: currentTeamId,
        },
        ...otherOptions,
      ];
    }

    return baseOptions;
  })();

  useEffect(() => {
    setLinkCopied(false);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
  }, [intakeId]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const copyIntakeLink = async () => {
    if (!intakeId || !team_id) {
      return;
    }
    const url = `${window.location.origin}/teams/${team_id}?tab=intake&selectedIntake=${intakeId}`;
    try {
      await navigator.clipboard.writeText(url);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      setLinkCopied(true);
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setLinkCopied(false);
        copyFeedbackTimeoutRef.current = null;
      }, 2000);
    } catch {
    }
  };

  const getOrgTeams = async () => {
    try {
      const res = await WebAppService.getOrgTeams();
      setTeams(res?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefetchIntakes = async () => {
    try {
      await onSuccess?.();
    } catch (error) {
      console.error('Error refreshing intakes after modal action:', error);
    }
  };

  const getIntakeDetails = async () => {
    if (!intakeId) return;

    setFetching(true);
    try {
      const res = await IntakeService.getIntakeById(intakeId);
      setIntakeDetails(res);
      setEditedValues({
        title: res?.title || '',
        description: res?.description || '',
        acceptance_criteria: res?.acceptance_criteria || '',
        test_cases: res?.test_cases || '',
      });
    } catch (error) {
      console.error(error);
    } finally {
      setFetching(false);
      markLoaded();
    }
  };

  const handleSave = async (field: string, value: any) => {
    setIntakeDetails((prev) => (prev ? { ...prev, [field]: value } : null));

    try {
      const payload: Record<string, any> = { [field]: value };
      await IntakeService.updateIntake(intakeId, payload);
    } catch (error) {
      await getIntakeDetails();
    } finally {
      await handleRefetchIntakes();
    }
  };

  const handleTransferIntakeToTeam = async (nextTeamId: string) => {
    if (
      !intakeId ||
      !nextTeamId ||
      getIntakeTeamId(intakeDetails) === nextTeamId
    ) {
      return;
    }

    const previousTeamId = getIntakeTeamId(intakeDetails);
    const selectedTeam = teamOptions.find((team) => team.value === nextTeamId);

    setIntakeDetails((prev) =>
      prev
        ? {
            ...prev,
            team_id: {
              _id: nextTeamId,
              team_name: selectedTeam?.label || (prev as any).team_name,
              team_code_name:
                selectedTeam?.label || (prev as any).team_code_name,
            },
          }
        : prev
    );

    try {
      const res = await IntakeService.transferIntakeToTeam(intakeId, {
        team_id: nextTeamId,
      });

      setIntakeDetails((prev) => (prev ? { ...prev, ...res } : prev));
    } catch (error) {
      setIntakeDetails((prev) =>
        prev
          ? {
              ...prev,
              team_id: {
                _id: previousTeamId,
                team_name: selectedTeam?.label || (prev as any).team_name,
                team_code_name:
                  selectedTeam?.label || (prev as any).team_code_name,
              },
            }
          : prev
      );
    } finally {
      await handleRefetchIntakes();
    }
  };

  const handleEditSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        title: editedValues.title,
        description: editedValues.description,
        acceptance_criteria: editedValues.acceptance_criteria,
        test_cases: editedValues.test_cases,
      };

      await IntakeService.updateIntake(intakeId, payload);

      setIntakeDetails((prev) => (prev ? { ...prev, ...payload } : null));

      setIsEditMode(false);
      await handleRefetchIntakes();
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditedValues({
      title: intakeDetails?.title || '',
      description: intakeDetails?.description || '',
      acceptance_criteria: intakeDetails?.acceptance_criteria || '',
      test_cases: intakeDetails?.test_cases || '',
    });
    setIsEditMode(false);
  };

  const handleClose = () => {
    closeModal('intake-details');
    setIsEditMode(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('selectedIntake');
      return next;
    });
  };

  useEffect(() => {
    if (intakeId && open) {
      getIntakeDetails();
      getOrgTeams();
    } else if (!intakeId) {
      setIntakeDetails(null);
    }
    if (!open) {
      resetLoaded();
    }
  }, [intakeId, open]);

  const breadcrumbItems = [
    { label: 'Team Intake', onClick: handleClose },
    { label: intakeDetails?.intake_code_name || 'Intake' },
  ];

  if (!open) return null;

  return (
    <CustomModalOverlay
      open={open}
      width={'50%'}
      className="min-w-[800px]"
      height="90vh"
      onClose={handleClose}
    >
      <div className="flex flex-col h-full w-full">
        {/* ===== Header ======= */}
        {hasLoadedOnce && (
          <header className="w-full flex-shrink-0 flex items-center justify-between gap-3 text-sm border-b border-modal-stroke pb-4">
            <div className="w-1/2">
              <p className="text-xs 2xl:text-sm text-input-inactive-placeholder"></p>
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <div className="w-fit flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={copyIntakeLink}
                className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors ${
                  linkCopied
                    ? 'text-[#40B18F]'
                    : 'text-input-inactive-placeholder hover:bg-frame-stroke hover:text-text-primary-highlight'
                }`}
                aria-label={
                  linkCopied ? 'Link copied' : 'Copy link to this intake'
                }
                title={linkCopied ? 'Link copied' : 'Copy link to this intake'}
              >
                {linkCopied ? (
                  <>
                    <Check size={18} strokeWidth={2.5} aria-hidden />
                    <span className="font-medium whitespace-nowrap">
                      Copied
                    </span>
                  </>
                ) : (
                  <CopyIcon size={18} aria-hidden />
                )}
              </button>
              {!isEditMode ? (
                <div className="w-full">
                  <GlobalButton
                    className={'whitespace-nowrap'}
                    onClick={() => setIsEditMode(true)}
                  >
                    Edit Intake
                  </GlobalButton>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div>
                    <GlobalButton
                      variant="stroke"
                      className={'whitespace-nowrap'}
                      onClick={handleEditCancel}
                    >
                      Cancel
                    </GlobalButton>
                  </div>
                  <div>
                    <GlobalButton
                      state={isSaving ? 'inactive' : 'default'}
                      className={'whitespace-nowrap'}
                      onClick={handleEditSave}
                    >
                      Save
                    </GlobalButton>
                  </div>
                </div>
              )}
            </div>
          </header>
        )}

        {/* ===== Content ======= */}
        <div className="flex-1 w-full">
          {!hasLoadedOnce ? (
            <div className="p-4 overflow-y-auto">
              {/* Metadata Grid Skeleton */}
              <div className="border border-modal-stroke rounded-md w-full mt-6 grid grid-cols-4">
                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-8 w-20" />
                </div>
                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-8 w-24" />
                </div>
                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-8 w-20" />
                </div>
                <div className="w-full p-3 flex flex-col gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-8 w-24" />
                </div>
              </div>

              {/* Title & Description Skeleton */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                <Skeleton className="h-5 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </div>

              {/* Acceptance Criteria Skeleton */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                <Skeleton className="h-5 w-32 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </div>

              {/* Test Cases Skeleton */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                <Skeleton className="h-5 w-24 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </div>

              {/* Request Type Skeleton */}
              <div className="mt-6 pb-4">
                <Skeleton className="h-5 w-20 mb-3" />
                <Skeleton className="h-10 w-full" />
              </div>

              {/* Links Section Skeleton */}
              <div className="mt-6">
                <Skeleton className="h-5 w-16 mb-3" />
                <Skeleton className="h-12 w-full" />
              </div>

              {/* Comments Section Skeleton */}
              <div className="mt-6 pb-8">
                <Skeleton className="h-5 w-20 mb-3" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ) : (
            <div className="p-4 overflow-y-auto">
              {/* Metadata Grid */}
              <div
                className="border border-modal-stroke rounded-md w-full mt-6 text-sm grid grid-cols-4"
                key={intakeId}
              >
                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <label
                    htmlFor="intake-assigned"
                    className="font-semibold text-input-active-label text-[10px] 2xl:text-xs"
                  >
                    Intake assigned
                  </label>

                  <AssignTask
                    type="intake"
                    selectedIntake={intakeDetails}
                    onAssign={handleRefetchIntakes}
                  />
                </div>

                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <label className="font-semibold text-input-active-label text-[10px] 2xl:text-xs">
                    Priority
                  </label>
                  <PopoverWithType
                    type="priority"
                    defaultValue={intakeDetails?.priority || 'P1'}
                    onChange={(val) => handleSave('priority', val)}
                    size="sm"
                  />
                </div>

                <div className="w-full p-3 flex flex-col gap-2 border-r border-modal-stroke">
                  <label className="font-semibold text-input-active-label text-[10px] 2xl:text-xs">
                    Status
                  </label>
                  <PopoverWithType
                    type="status"
                    defaultValue={intakeDetails?.intake_status || 'To-Do'}
                    onChange={(val) => handleSave('intake_status', val)}
                    size="sm"
                  />
                </div>

                <div className="w-full p-3 flex flex-col gap-2">
                  <label
                    htmlFor="department"
                    className="font-semibold text-input-active-label text-[10px] 2xl:text-xs flex items-center gap-1"
                  >
                    <div className="w-4 text-input-inactive-placeholder">
                      <DeptIcon />
                    </div>
                    Department
                  </label>
                  <PopoverWithType
                    type="department"
                    defaultValue={(intakeDetails as any)?.intake_department || ''}
                    onChange={(val) => handleSave('intake_department', val)}
                    size="sm"
                  />
                </div>
              </div>

              {/* Title & Description */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                {isEditMode ? (
                  <>
                    <TextInput
                      label="Title"
                      value={editedValues.title}
                      onChange={(e) =>
                        setEditedValues((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Enter intake title"
                    />
                    <div className="mt-3">
                      <TextArea
                        label="Description"
                        value={editedValues.description}
                        onChange={(e) =>
                          setEditedValues((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                        placeholder="Enter intake description"
                        rows={3}
                        height="120px"
                        withMaxLength={false}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
                      {intakeDetails?.title}
                    </h3>
                    <p className="text-sm 2xl:text-sm mt-2 text-input-inactive-placeholder">
                      {intakeDetails?.description}
                    </p>
                  </>
                )}
              </div>

              {/* Acceptance Criteria */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                {isEditMode ? (
                  <TextArea
                    label="Acceptance criteria"
                    value={editedValues.acceptance_criteria}
                    onChange={(e) =>
                      setEditedValues((prev) => ({
                        ...prev,
                        acceptance_criteria: e.target.value,
                      }))
                    }
                    placeholder="Enter acceptance criteria (one per line)"
                    rows={3}
                    height="120px"
                    withMaxLength={false}
                  />
                ) : (
                  <>
                    <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
                      Acceptance criteria
                    </h3>
                    {intakeDetails?.acceptance_criteria
                      ?.split('\n')
                      .map((line: string, i: number) => (
                        <p
                          key={i}
                          className="mb-0.5 last:mb-0 text-sm 2xl:text-sm mt-2 text-input-inactive-placeholder"
                        >
                          {line}
                        </p>
                      ))}
                  </>
                )}
              </div>

              {/* Test Cases */}
              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke">
                {isEditMode ? (
                  <TextArea
                    label="Test cases"
                    value={editedValues.test_cases}
                    onChange={(e) =>
                      setEditedValues((prev) => ({
                        ...prev,
                        test_cases: e.target.value,
                      }))
                    }
                    placeholder="Enter test cases (one per line)"
                    rows={3}
                    height="120px"
                    withMaxLength={false}
                  />
                ) : (
                  <>
                    <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold">
                      Test cases
                    </h3>
                    {intakeDetails?.test_cases
                      ?.split('\n')
                      .map((line: string, i: number) => (
                        <p
                          key={i}
                          className="mb-0.5 last:mb-0 text-sm 2xl:text-sm mt-2 text-input-inactive-placeholder"
                        >
                          {line}
                        </p>
                      ))}
                  </>
                )}
              </div>

              <div className="mt-6 pb-4 border-b-0.5 border-modal-stroke w-full">
                <h3 className="text-sm 2xl:text-base text-input-active-label font-semibold mb-2">
                Team
                </h3>
                <TeamSelectorModal
                  teams={teams}
                  selectedTeamId={getIntakeTeamId(intakeDetails)}
                  selectedTeamName={getIntakeTeamName(intakeDetails)}
                  onChange={handleTransferIntakeToTeam}
                />
              </div>

              {/* Intake Type */}
              <div className="mt-6 pb-4">
                <SelectInput
                  size="lg"
                  label="Intake type"
                  placeholder="Select intake type"
                  options={INTAKE_TYPE_OPTIONS}
                  value={intakeDetails?.intake_type || ''}
                  onChange={(val) => handleSave('intake_type', val)}
                />
              </div>

              {/* Attachments Section */}
              <div className="mt-6">
                <Attachments
                  parent={
                    intakeId ? { type: 'intake', intakeId } : null
                  }
                />
              </div>

              {/* Links Section */}
              <div className="mt-6">
                <IntakeLinks intake={intakeDetails} />
              </div>

              {/* Comments Section */}
              <div className="mt-6 pb-8">
                <IntakeComments intake={intakeDetails} />
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModalOverlay>
  );
}
